import { FC, useContext, useEffect, useState } from 'react';
import { ObraProperties, WandaProject } from '../../../types/ObraDetails';
import { ValidationError } from '../../../types/ValidationError';
import { UserModeContext } from '../../../context/UserModeProvider';
import ViewLocalizationEnvironment from './viewLocalizationEnvironment/viewLocalizationEnvironment';
import EditLocalizationEnvironment from './editLocalizationEnvironment/editLocalizationEnvironment';
import styles from './localizationEnvironment.module.scss';
import { DetailsViewContext } from '../../../context/DetailsViewProvider';
import Map from '../../../components/Map/Map';
import MarkerLayer from '../../../components/Map/MapLayers/Marker/MarkerLayer';
import KMZLayer from '../../../components/Map/MapLayers/KMZ/KMZLayer';
import CustomControl from '../../../components/Map/MapLayers/CustomControl/CustomControl';
import KMZButton from './components/kmzButton/kmzButton';
import { useTranslation } from 'react-i18next';
import { SaveContext } from '../../../context/SaveProvider';
import DialogError from '../../../components/dialogError/DialogError';
import ObrasAPI from '../../../services/APIObras';
import ErrorDetails from '../components/ErrorDetails/ErrorDetails';
import LocalizationLoadingSkeleton from './components/localizationLoadingSkeleton/localizationLoadingSkeleton';


interface LocalizationEnvironmentProps {
  obra: ObraProperties;
  codCentro: string;
  wandaProject?: WandaProject;
  wandaProjectLoading: boolean;
  wandaProjectError: boolean;
  version: 'viewer' | 'editor';
  errors?: ValidationError[];
}

/* eslint-disable @typescript-eslint/no-non-null-assertion */
const LocalizationEnvironment: FC<LocalizationEnvironmentProps> = (props) => {
  const {
    obra,
    codCentro,
    wandaProject,
    wandaProjectLoading,
    wandaProjectError,
    version,
    errors,
  } = props;
  const [t, i18n] = useTranslation();
  const {
    mapIsSelected,
    setObra,
    loadedDetailSections,
    markDetailSectionLoaded,
  } = useContext(DetailsViewContext);
  const userModeContext = useContext(UserModeContext);
  const saveContext = useContext(SaveContext);
  const isEditorMode = userModeContext.userMode === 'editor';
  const [kmz, setKmz] = useState(obra?.localizacion?.kmz);
  const [kmzLoadingError, setKmzLoadingError] = useState(false);
  const [locationLoaded, setLocationLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const sectionKey = `${codCentro}:${version}:${i18n.language}:location`;

  useEffect(() => {
    let active = true;
    setLocationLoaded(false);
    setLoadError(false);
    if (!wandaProject) return () => {
      active = false;
    };

    if (loadedDetailSections[sectionKey]) {
      setLocationLoaded(true);
      return () => {
        active = false;
      };
    }

    ObrasAPI.getInstance().getObraLocationAPI(codCentro, wandaProject, version)
      .then((response) => {
        if (!active) return;
        setObra({
          ...obra,
          general: {
            ...obra.general,
            codPais: response.general.codPais,
            nomPais: response.general.nomPais,
          },
          localizacion: response.localizacion,
        });
        markDetailSectionLoaded(sectionKey);
        setLocationLoaded(true);
      })
      .catch(() => {
        if (active) setLoadError(true);
      });

    return () => {
      active = false;
    };
  }, [codCentro, wandaProject, version, i18n.language, saveContext.reloadData, loadedDetailSections, markDetailSectionLoaded, sectionKey]);

  useEffect(() => {
    setKmz(obra?.localizacion?.kmz);
  }, [obra?.localizacion?.kmz]);

  const handleChangeKMZ = (newkmz: string) => {
    setKmzLoadingError(false);
    setKmz(newkmz);
  };

  const callbackKmzLayer = (res?: string) => {
    if (isEditorMode) {
      if (res === 'INVALID_DOCUMENT') {
        setKmzLoadingError(true);
      } else if (res && res !== obra?.localizacion?.kmz) {
        obra.localizacion.kmz = res;
        saveContext.handleChangeSave(true);
      }
    }
  };

  if (loadError || wandaProjectError) return <ErrorDetails />;
  if (!locationLoaded || wandaProjectLoading) return <LocalizationLoadingSkeleton />;

  return (
    <div
      data-testid="locationEnvironment-test"
      className={styles.localizationEnvironment}
      style={mapIsSelected ? { display: 'block' } : { display: 'none' }}
    >
      {/* MAP SECTION  */}
      {obra.localizacion?.coordenadas && <div className={`${styles.contentMap} content-map`}>
        <Map context={DetailsViewContext}>
          {(mapInstance: google.maps.Map) => (
            <>
              <MarkerLayer mapInstance={mapInstance} coordinates={obra?.localizacion?.coordenadas} />
              <KMZLayer mapInstance={mapInstance} url={kmz} obra={obra} callback={callbackKmzLayer} />
              <CustomControl mapInstance={mapInstance} show={isEditorMode} >
                <KMZButton obra={obra} t={t} handleChange={handleChangeKMZ} />
              </CustomControl>
              {kmzLoadingError && <DialogError error={t('details.location.invalidKMZ') as string} />}
            </>
          )}
        </Map>

      </div>}

      {isEditorMode ? <EditLocalizationEnvironment obra={obra} errors={errors} /> : <ViewLocalizationEnvironment obra={obra} />}
      <div className='footer' style={{marginBottom: '75px'}}></div>
    </div>);
};

export default LocalizationEnvironment;
